import { ServiceBase } from "@app/shared";
import { SortDirections } from "@app/shared/waffles";
import { AddTemplateModel, TemplateMetadataModel, TemplateCollectionMetadataListModel, TemplateHistoryModel, TemplateCollectionMetadataModelWithActiveProjects, UpdateTemplateModel } from "./template";
import { MinimalUser } from "../user-administration/userModel";
import { querystringify } from "@app/shared/utility";
import { TemplateDetailsData } from "../manage-specialties";

export default class TemplateService extends ServiceBase {

  private authenticationApiEndpoint = `${this.apiEndpoint}`;

  public async getAllTemplateCollections(): Promise<TemplateCollectionMetadataModelWithActiveProjects[]> {
    const data = await this.makeGet<TemplateCollectionMetadataModelWithActiveProjects[]>(`/templates/nonUniversalTemplateCollectionsWithActiveProjects/`, null, this.authenticationApiEndpoint);
    return data;
  }

  public async getTemplatesById(templateCollectionId: number): Promise<TemplateCollectionMetadataModelWithActiveProjects> {
    const data = await this.makeGet<TemplateCollectionMetadataModelWithActiveProjects>(`/templates/` + templateCollectionId.toString(), null, this.authenticationApiEndpoint);
    return data;
  }

  public async getTemplatesByClientId(clientId: number, activeOnly: boolean, includeUsers: boolean, includeEditUsers: boolean, includeTemplates: boolean, skip: number, take: number, sortDirection: SortDirections = SortDirections.None, sortedColumn?: string, templateNameFilter?: string, clientNameFilter?: string, templateFilter: number[] = []): Promise<TemplateCollectionMetadataListModel> {
    const query = {
      activeOnly,
      clientNameFilter,
      includeEditUsers,
      includeTemplates,
      includeUsers,
      skip,
      sortDirection,
      sortedColumn,
      take,
      templateFilter,
      templateNameFilter,
    };

    return await this.makeGet<TemplateCollectionMetadataListModel>(`/manage-templates/clients/${clientId}?${querystringify(query)}`, null, this.authenticationApiEndpoint);
  }

  public async getTemplateHistory(templateCollectionId: number, correlationId: string): Promise<TemplateHistoryModel> {
    return await this.makeGet<TemplateHistoryModel>(`/templates/${templateCollectionId}/correlationIds/${encodeURIComponent(correlationId)}/history`, null, this.authenticationApiEndpoint);
  }

  public async addTemplate(model: AddTemplateModel): Promise<TemplateMetadataModel> {
    return await this.makePost<TemplateMetadataModel>(`/manage-templates`, model, this.authenticationApiEndpoint);
  }

  public async addTemplateJson(model: TemplateDetailsData): Promise<void> {
    return await this.makePost<void>(`/templates/${model.templateCollectionId.toString()}/template/addjson`, model, this.authenticationApiEndpoint);
  }

  public async addTemplateComment(id: number, templateId: string, comment: string): Promise<void> {
    return await this.makePost<void>(`/templates/${id.toString()}/template/${templateId}/addcomment`, comment, this.authenticationApiEndpoint)
  }

  public async updateTemplate(templateId: number, model: UpdateTemplateModel): Promise<TemplateMetadataModel> {
    return await this.makePut<TemplateMetadataModel>(`/manage-templates/${templateId}`, model, this.authenticationApiEndpoint);
  }

  public async activateTemplate(templateId: number, isActive: boolean): Promise<void> {
    const query = {
      isActive,
    };

    return await this.makePatch<void>(`/manage-templates/${templateId}/activate?${querystringify(query)}`, null, false, this.authenticationApiEndpoint);
  }

  public async ArchiveTemplate(id: number, templateId: string): Promise<void> {
    return await this.makePost(`/templates/archive/collection/${id}/template/${templateId}`);
  }

  public async UnarchiveTemplate(id: number, templateId: string): Promise<void> {
    return await this.makePost(`templates/unarchive/collection/${id}/template/${templateId}`);
  }

  public async ArchiveTemplateCollection(id: number): Promise<void> {
    return await this.makePatch(`/templates/archive/collection/${id}`);
  }

  public async UnarchiveTemplateCollection(id: number): Promise<void> {
    return await this.makePatch(`templates/unarchive/collection/${id}`);
  }

  public async templateNameExists(name: string, clientId: number, templateId?: number): Promise<boolean> {
    const query = {
      clientId,
      templateId
    };

    return await this.makeGet<boolean>(`/manage-templates/exists/${name}?${querystringify(query)}`, null, this.authenticationApiEndpoint);
  }

  public async getTemplatesByUserId(userId: number, activeOnly: boolean, includeTemplates: boolean): Promise<TemplateMetadataModel[]> {
    const query = {
      activeOnly,
      includeTemplates
    };

    return await this.makeGet<TemplateMetadataModel[]>(`/manage-templates/users/${userId}?${querystringify(query)}`, null, this.authenticationApiEndpoint);
  }

  public async getUsersAuthorizedForEdit(clientId: number): Promise<MinimalUser[]> {
    return await this.makeGet<MinimalUser[]>(`/manage-templates/clients/${clientId}/authorized-users`, null, this.authenticationApiEndpoint);
  }
}
